import useFetchAppPreferences from '@/core/hooks/apiHooks/useFetchAppPreferences';
import usePatchAppPreferences from '@/core/hooks/apiHooks/usePatchAppPreferences';
import { useUser } from '@/core/contexts/app.context';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import PreferencesAdapter from '../adapters/preferences_adapter';

const getLocalKey = (userId: string) => `preferences_updated_at_${userId}`;

const getUpdatedAt = (userId: string) => {
  const localKey = getLocalKey(userId);
  return localStorage.getItem(localKey) ?? dayjs().subtract(1, 'year').toISOString();
};

const saveUpdatedAt = (userId: string, updatedAt: string) => {
  const localKey = getLocalKey(userId);
  localStorage.setItem(localKey, updatedAt);
};

/**
 * This hook fetches and patches the remote preferences.
 * Should not be reused.
 *
 * Syncs local and remote preferences based on the updated_at property.
 * The hook tries to update remote or local preferences depending on which update_at is bigger
 *
 * Ensures to sync preferences if needed on window focus and
 * network connection recovery.
 *
 * @returns {canSyncPreferences, syncPreferences, updateAppPreferences}
 */
const useSyncPreferences = () => {
  const { data, isLoading, isSuccess, isRefetching, refetch } = useFetchAppPreferences();
  const updatePreferences = usePatchAppPreferences();
  const { state: user } = useUser();

  const updateAppPreferences = (preferences: any) => {
    if (!user?.id) return;

    saveUpdatedAt(user.id.toString(), new Date().toISOString());
    updatePreferences.mutate({
      updated_at: getUpdatedAt(user.id.toString()),
      ...PreferencesAdapter.toRemote({ ...preferences }),
    });
  };

  const syncPreferences = (
    currentPreferences: any,
    savePreferences: (preferences: any) => void,
  ) => {
    if (!user?.id) return;

    const { data: preferences } = data;
    if (!preferences) {
      updateAppPreferences(currentPreferences);
      return;
    }

    const remoteUpdatedAt = dayjs(new Date(preferences.updated_at));
    const remoteCreatedAt = dayjs(new Date(preferences.created_at));

    if (remoteCreatedAt.isSame(remoteUpdatedAt)) {
      updateAppPreferences(currentPreferences);
      return;
    }

    const localUpdatedAt = dayjs(new Date(getUpdatedAt(user.id.toString())));

    if (remoteUpdatedAt.isBefore(localUpdatedAt)) {
      updateAppPreferences(currentPreferences);
    } else if (remoteUpdatedAt.isAfter(localUpdatedAt)) {
      saveUpdatedAt(user.id.toString(), preferences.updated_at);
      savePreferences(PreferencesAdapter.toLocal(preferences));
    }
  };

  useEffect(() => {
    const onChange = () => refetch();

    window.addEventListener('focus', onChange);
    window.addEventListener('online', onChange);

    return () => {
      window.removeEventListener('focus', onChange);
      window.removeEventListener('online', onChange);
    };
  }, []);

  useEffect(() => {
    if (!updatePreferences.isSuccess || !user?.id) {
      return;
    }

    const preferences = updatePreferences.data.data;
    const updatedAt = new Date(preferences.updated_at).toISOString();

    saveUpdatedAt(user.id.toString(), updatedAt);
    refetch();
  }, [updatePreferences.data, updatePreferences.isSuccess, user?.id]);

  return {
    canSyncPreferences: !isLoading && !isRefetching && isSuccess && !!data,
    syncPreferences,
    updateAppPreferences,
  };
};

export default useSyncPreferences;
